import { apiClient, ApiError } from '../lib/apiClient';

export interface LivestockRecord {
  id: string;
  aquariumId: string;
  userId: string;
  commonName: string;
  scientificName: string | null;
  category: 'coral' | 'fish' | 'invertebrate' | 'other';
  status: 'active' | 'archived' | 'deceased' | 'quarantine';
  dateAdded: string;
  purchaseDate: string | null;
  purchasedFrom: string | null;
  purchasePrice: number | null;
  citesNumber: string | null;
  notes: string | null;
  /** Date of death (YYYY-MM-DD) while status is 'deceased'; null otherwise or when not recorded. */
  deceasedAt: string | null;
  /** Optional cause / notes on the death. */
  deceasedReason: string | null;
  imageIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateLivestockInput {
  commonName: string;
  scientificName?: string | null;
  category: 'coral' | 'fish' | 'invertebrate' | 'other';
  status: 'active' | 'archived' | 'deceased' | 'quarantine';
  dateAdded: string;
  purchaseDate?: string | null;
  purchasedFrom?: string | null;
  purchasePrice?: number | null;
  citesNumber?: string | null;
  notes?: string | null;
  deceasedAt?: string | null;
  deceasedReason?: string | null;
}

export interface UpdateLivestockInput {
  commonName: string;
  scientificName?: string | null;
  category: 'coral' | 'fish' | 'invertebrate' | 'other';
  status: 'active' | 'archived' | 'deceased' | 'quarantine';
  dateAdded: string;
  purchaseDate?: string | null;
  purchasedFrom?: string | null;
  purchasePrice?: number | null;
  citesNumber?: string | null;
  notes?: string | null;
  deceasedAt?: string | null;
  deceasedReason?: string | null;
}

export interface PatchLivestockInput {
  commonName?: string;
  scientificName?: string | null;
  category?: 'coral' | 'fish' | 'invertebrate' | 'other';
  status?: 'active' | 'archived' | 'deceased' | 'quarantine';
  dateAdded?: string;
  purchaseDate?: string | null;
  purchasedFrom?: string | null;
  purchasePrice?: number | null;
  citesNumber?: string | null;
  notes?: string | null;
  deceasedAt?: string | null;
  deceasedReason?: string | null;
}

export interface ListLivestockParams {
  category?: 'coral' | 'fish' | 'invertebrate' | 'other';
  status?: 'active' | 'archived' | 'deceased' | 'quarantine';
  sort?: 'date_added' | 'common_name' | 'category';
}

interface ApiResponse<T> {
  data: T;
}

const CATEGORIES: readonly LivestockRecord['category'][] = ['coral', 'fish', 'invertebrate', 'other'];
const STATUSES: readonly LivestockRecord['status'][] = ['active', 'archived', 'deceased', 'quarantine'];

function pick(raw: Record<string, unknown>, camel: string, snake: string): unknown {
  return raw[camel] !== undefined ? raw[camel] : raw[snake];
}

function str(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/** YYYY-MM-DD from a date-only or ISO timestamp string (`<input type="date">` needs the former). */
function dateOnly(value: unknown): string | null {
  const s = str(value);
  if (!s) return null;
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

function price(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalises whatever the API sends into a complete `LivestockRecord`: camelCase
 * or the raw snake_case row (older backends), `imageIds` always an array, unknown
 * category/status mapped to a renderable fallback. The livestock page crashed
 * (blank screen) when `imageIds` was missing — never trust the payload shape.
 */
export function normalizeLivestockRecord(input: unknown): LivestockRecord {
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const category = pick(raw, 'category', 'category');
  const status = pick(raw, 'status', 'status');
  const imageIds = pick(raw, 'imageIds', 'image_ids');
  return {
    id: str(raw.id) ?? '',
    aquariumId: str(pick(raw, 'aquariumId', 'aquarium_id')) ?? '',
    userId: str(pick(raw, 'userId', 'user_id')) ?? '',
    commonName: str(pick(raw, 'commonName', 'common_name')) ?? '',
    scientificName: str(pick(raw, 'scientificName', 'scientific_name')) || null,
    category: CATEGORIES.includes(category as LivestockRecord['category'])
      ? (category as LivestockRecord['category'])
      : 'other',
    status: STATUSES.includes(status as LivestockRecord['status'])
      ? (status as LivestockRecord['status'])
      : 'active',
    dateAdded: dateOnly(pick(raw, 'dateAdded', 'date_added')) ?? '',
    purchaseDate: dateOnly(pick(raw, 'purchaseDate', 'purchase_date')),
    purchasedFrom: str(pick(raw, 'purchasedFrom', 'purchased_from')),
    purchasePrice: price(pick(raw, 'purchasePrice', 'purchase_price')),
    citesNumber: str(pick(raw, 'citesNumber', 'cites_number')),
    notes: str(raw.notes),
    deceasedAt: dateOnly(pick(raw, 'deceasedAt', 'deceased_at')),
    deceasedReason: str(pick(raw, 'deceasedReason', 'deceased_reason')),
    imageIds: Array.isArray(imageIds) ? imageIds.filter((id): id is string => typeof id === 'string') : [],
    createdAt: str(pick(raw, 'createdAt', 'created_at')) ?? '',
    updatedAt: str(pick(raw, 'updatedAt', 'updated_at')) ?? '',
  };
}

export async function listLivestock(
  aquariumId: string,
  params?: ListLivestockParams,
): Promise<LivestockRecord[]> {
  const queryParams = new URLSearchParams();
  if (params?.category) {
    queryParams.set('category', params.category);
  }
  if (params?.status) {
    queryParams.set('status', params.status);
  }
  if (params?.sort) {
    queryParams.set('sort', params.sort);
  }

  const query = queryParams.toString();
  const url = `/api/v1/aquariums/${aquariumId}/livestock${query ? `?${query}` : ''}`;
  try {
    const response = await apiClient.get<ApiResponse<unknown>>(url);
    const items = response?.data;
    return Array.isArray(items) ? items.map(normalizeLivestockRecord) : [];
  } catch (err) {
    // The backend returns 404 when the aquarium has no livestock yet — treat as empty.
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}

export async function getLivestock(
  aquariumId: string,
  livestockId: string,
): Promise<LivestockRecord> {
  const response = await apiClient.get<ApiResponse<unknown>>(
    `/api/v1/aquariums/${aquariumId}/livestock/${livestockId}`,
  );
  return normalizeLivestockRecord(response?.data);
}

export async function createLivestock(
  aquariumId: string,
  input: CreateLivestockInput,
): Promise<LivestockRecord> {
  const response = await apiClient.post<ApiResponse<unknown>>(
    `/api/v1/aquariums/${aquariumId}/livestock`,
    {
      common_name: input.commonName,
      scientific_name: input.scientificName ?? null,
      category: input.category,
      status: input.status,
      date_added: input.dateAdded,
      purchase_date: input.purchaseDate ?? null,
      purchased_from: input.purchasedFrom ?? null,
      purchase_price: input.purchasePrice ?? null,
      cites_number: input.citesNumber ?? null,
      notes: input.notes ?? null,
      ...(input.deceasedAt !== undefined ? { deceased_at: input.deceasedAt } : {}),
      ...(input.deceasedReason !== undefined ? { deceased_reason: input.deceasedReason } : {}),
    },
  );
  return normalizeLivestockRecord(response?.data);
}

export async function updateLivestock(
  aquariumId: string,
  livestockId: string,
  input: UpdateLivestockInput,
): Promise<LivestockRecord> {
  const response = await apiClient.put<ApiResponse<unknown>>(
    `/api/v1/aquariums/${aquariumId}/livestock/${livestockId}`,
    {
      common_name: input.commonName,
      scientific_name: input.scientificName ?? null,
      category: input.category,
      status: input.status,
      date_added: input.dateAdded,
      purchase_date: input.purchaseDate ?? null,
      purchased_from: input.purchasedFrom ?? null,
      purchase_price: input.purchasePrice ?? null,
      cites_number: input.citesNumber ?? null,
      notes: input.notes ?? null,
      // Only sent when given: the API keeps a deceased animal's date of death otherwise.
      ...(input.deceasedAt !== undefined ? { deceased_at: input.deceasedAt } : {}),
      ...(input.deceasedReason !== undefined ? { deceased_reason: input.deceasedReason } : {}),
    },
  );
  return normalizeLivestockRecord(response?.data);
}

export async function patchLivestock(
  aquariumId: string,
  livestockId: string,
  input: PatchLivestockInput,
): Promise<LivestockRecord> {
  // The API validates snake_case fields (PartialUpdateLivestockSchema).
  const body: Record<string, unknown> = {};
  const fields: Array<[keyof PatchLivestockInput, string]> = [
    ['commonName', 'common_name'],
    ['scientificName', 'scientific_name'],
    ['category', 'category'],
    ['status', 'status'],
    ['dateAdded', 'date_added'],
    ['purchaseDate', 'purchase_date'],
    ['purchasedFrom', 'purchased_from'],
    ['purchasePrice', 'purchase_price'],
    ['citesNumber', 'cites_number'],
    ['notes', 'notes'],
    ['deceasedAt', 'deceased_at'],
    ['deceasedReason', 'deceased_reason'],
  ];
  for (const [key, apiKey] of fields) {
    if (input[key] !== undefined) body[apiKey] = input[key];
  }
  const response = await apiClient.patch<ApiResponse<unknown>>(
    `/api/v1/aquariums/${aquariumId}/livestock/${livestockId}`,
    body,
  );
  return normalizeLivestockRecord(response?.data);
}

export async function deleteLivestock(
  aquariumId: string,
  livestockId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/livestock/${livestockId}`);
}

export async function linkLivestockImage(
  aquariumId: string,
  livestockId: string,
  imageId: string,
  options: { main?: boolean } = {},
): Promise<void> {
  await apiClient.post(
    `/api/v1/aquariums/${aquariumId}/livestock/${livestockId}/images`,
    options.main ? { imageId, main: true } : { imageId },
  );
}

/** Makes a linked image the animal's main photo (shown in the livestock list). */
export async function setMainLivestockImage(
  aquariumId: string,
  livestockId: string,
  imageId: string,
): Promise<void> {
  await apiClient.put(`/api/v1/aquariums/${aquariumId}/livestock/${livestockId}/images/${imageId}/main`, {});
}

export async function unlinkLivestockImage(
  aquariumId: string,
  livestockId: string,
  imageId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/aquariums/${aquariumId}/livestock/${livestockId}/images/${imageId}`,
  );
}

/**
 * Thumbnail URL of an aquarium image (thumbnail → display → original), or null
 * when it cannot be loaded — the caller then shows its placeholder.
 */
export async function fetchLivestockImageThumbnailUrl(aquariumId: string, imageId: string): Promise<string | null> {
  try {
    const resp = await apiClient.get<{
      data: { thumbnailUrl?: string | null; displayUrl?: string | null; originalUrl?: string | null };
    }>(`/api/v1/aquariums/${aquariumId}/images/${imageId}`);
    return resp.data?.thumbnailUrl ?? resp.data?.displayUrl ?? resp.data?.originalUrl ?? null;
  } catch {
    return null;
  }
}
